// ============================================================
// constants/navCategories.js  — v2 재정리
// EV Powertrain Simulator — 네비게이션 카테고리·섹션 정의
// 참조: ISO 26262, ISO/SAE 21434, ISO 21448, WLTP UN/ECE R154
// ============================================================
// 탭 재분류 원칙:
//   1. sim  🚀 시뮬레이션  — 코어 시뮬(주행·사이클·가감속)
//   2. veh  ⚙️ 차량 시스템 — 서스펜션·변속기·AWD·환경·비교
//   3. pt   🔋 파워트레인·배터리 — 에너지체인·열관리·배터리·충전·에너지관리
//   4. ha   🛡️ 기능안전·보안 — ISO 26262/21434/21448 순서 정렬
//   5. op   📊 최적화·설계 — 민감도·DOE·설계권고·파라미터매트릭스
// ============================================================

/**
 * NAV_CATEGORIES — 앱 전체 네비게이션 구조 정의
 * 출처: 내부 UX 설계 (v2 재분류)
 * 사용처: ev-sim_v2_0.jsx 좌측 사이드바, i18n.js 섹션 레이블 매핑
 * 구조: id·label·labelKo·color·sections[] 배열로 구성.
 *       sections[].id는 탭 라우팅 키로 사용 (TabContent 컴포넌트 분기 기준).
 */
export const NAV_CATEGORIES = [
  {
    id: "sim",
    label: "🚀 Simulation",
    labelKo: "🚀 시뮬레이션",
    color: "#f97316",
    sections: [
      { id: "launch",       label: "Launch (0→100)",    labelKo: "발진 (0→100)",       icon: "🚀" },
      { id: "reaccel",      label: "Re-Accel",          labelKo: "재가속",              icon: "⚡" },
      { id: "coast",        label: "Coast",             labelKo: "코스팅",              icon: "🌀" },
      { id: "braking",      label: "Regen Brake",       labelKo: "회생제동",            icon: "🛑" },
      { id: "wltp",         label: "Driving Cycle",     labelKo: "주행 사이클 (WLTP)", icon: "📈" },
      { id: "driveability", label: "Drivability",       labelKo: "승차감·저크 분석",    icon: "🎛️" },
      { id: "roadload",     label: "Road Load",         labelKo: "정속 도로 부하",      icon: "🛣️" },
    ],
  },
  {
    id: "veh",
    label: "⚙️ Vehicle Systems",
    labelKo: "⚙️ 차량 시스템",
    color: "#f59e0b",
    sections: [
      { id: "suspension4w", label: "Suspension / Rollover", labelKo: "서스펜션·롤오버·VD제어", icon: "🛞" },
      { id: "gearbox",      label: "Gearbox · Shift Jerk",  labelKo: "다단 변속기 · 변속 저크",   icon: "⚙️" },
      { id: "awd_tv",       label: "AWD / Torque Vec.",      labelKo: "AWD·토크벡터링",          icon: "🔀" },
      { id: "roadenv",      label: "Road & Environment",    labelKo: "도로·환경 조건",           icon: "🌍" },
      { id: "compare",      label: "Vehicle Compare",       labelKo: "차량 비교",               icon: "⚖️" },
    ],
  },
  {
    id: "pt",
    label: "🔋 Powertrain & Battery",
    labelKo: "🔋 파워트레인·배터리",
    color: "#0ea5e9",
    sections: [
      { id: "energy",       label: "Energy Chain",          labelKo: "에너지 체인·손실",    icon: "🔋" },
      { id: "thermal",      label: "Thermal Mgmt",          labelKo: "열관리",               icon: "🌡️" },
      { id: "degradation",  label: "Battery Health / SOH",  labelKo: "배터리 건강·SOH",      icon: "🧪" },
      { id: "enhanced",      label: "Advanced Physics",      labelKo: "고급 물리 모델",       icon: "🧠" },
      { id: "control_loop",  label: "Control Loop Analysis", labelKo: "제어 루프 분석",       icon: "🔁" },
      { id: "charging",      label: "Charging Sim",          labelKo: "충전 시뮬레이션",      icon: "🔌" },
      { id: "energy_mgmt",   label: "Energy Mgmt (PEMS)",    labelKo: "예측 에너지 관리",     icon: "📡" },
    ],
  },
  {
    id: "ha",
    label: "🛡️ Safety & Security",
    labelKo: "🛡️ 기능안전·보안",
    color: "#10b981",
    sections: [
      { id: "hara",                     label: "① HARA / Functional Safety",        labelKo: "① 개념 단계 (HARA·기능안전)",            icon: "🧰" },
      { id: "technical_safety_concept",  label: "② Tech. Safety (TSR)",          labelKo: "② 기술안전 개념 (TSR)",          icon: "🔧" },
      { id: "fmea_safety",               label: "③ FMEA / FTA Analysis",           labelKo: "③ 시스템 안전 (FMEA·FTA)",       icon: "🧷" },
      { id: "hw_safety",          label: "④ HW Safety Design",          labelKo: "④ HW 안전 설계 (Part 5)",          icon: "🔩" },
      { id: "sw_safety",          label: "⑤ SW Safety Design",          labelKo: "⑤ SW 안전 설계 (Part 6)",          icon: "💻" },
      { id: "cybersecurity_tara", label: "⑥ Cybersecurity TARA",        labelKo: "⑥ 사이버보안 TARA (21434)",        icon: "🔐" },
      { id: "sotif_iso21448",     label: "⑦ SOTIF (ISO 21448)",         labelKo: "⑦ SOTIF 분석 (21448)",            icon: "🎯" },
      { id: "traceability_case",  label: "⑧ Safety Case",               labelKo: "⑧ 추적·안전 케이스",      icon: "🔗" },
      { id: "safety_verification", label: "⑨ Safety V&V",                labelKo: "⑨ 안전 검증 (V&V)",        icon: "🔬" },
    ],
  },
  {
    id: "op",
    label: "📊 Optimization & Design",
    labelKo: "📊 최적화·설계",
    color: "#8b5cf6",
    sections: [
      { id: "sensitivity",       label: "Sensitivity Analysis",  labelKo: "민감도 분석",                    icon: "🎚️" },
      { id: "passfail",          label: "Pass / Fail",           labelKo: "합격·불합격 판정",               icon: "✅" },
      { id: "doe",               label: "DOE / Bayesian",        labelKo: "DOE·베이즈 최적화",              icon: "🧮" },
      { id: "analysis_overview", label: "Analysis Overview",     labelKo: "통합 분석",                      icon: "📚" },
      { id: "param_matrix",      label: "Parameter Matrix",      labelKo: "파라미터 상호 영향 매트릭스",    icon: "🔢" },
      { id: "design_reco",       label: "Design Recommendations",labelKo: "설계 권고사항",                  icon: "📋" },
      { id: "domain_audit",      label: "Domain Completeness",   labelKo: "도메인 완성도",                  icon: "🗂️" },
      { id: "dev_history",       label: "Dev History",           labelKo: "개발 이력",                      icon: "📋" },
    ],
  },
];

/**
 * AF_CATS — NAV_CATEGORIES 하위 호환 별칭
 * 구버전 코드에서 AF_CATS로 참조하던 곳을 대체하기 위한 re-export.
 * 신규 코드는 NAV_CATEGORIES를 직접 import 할 것.
 */
export const AF_CATS = NAV_CATEGORIES;